import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, HeartPulse, ShieldPlus, CalendarCheck, User, Menu, X, MapPin, Bell, Wrench, Wallet, Scale, LogOut, Users } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ModeSwitch } from "@/components/ModeSwitch";
import { LanguageToggle } from "@/components/LanguageToggle";
import { WhatsAppAssistant } from "@/components/WhatsAppAssistant";
import { LocationSheet } from "@/components/LocationSheet";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import api from "@/lib/api";
import { useGeo, geoLabel } from "@/lib/geo";
import { CareAreaBanner } from "@/components/CareAreaBanner";
import { CareSosButton } from "@/components/CareSosButton";

const CARE_NAV = [
    { to: "/care", label: "Home", icon: Home, testid: "care-nav-home", exact: true },
    { to: "/care/plans", label: "Care Plan", icon: ShieldPlus, testid: "care-nav-plans" },
    { to: "/care/health", label: "Health", icon: HeartPulse, testid: "care-nav-health" },
    { to: "/care/bookings", label: "Bookings", icon: CalendarCheck, testid: "care-nav-bookings" },
    { to: "/profile", label: "Profile", icon: User, testid: "care-nav-profile" },
];

export function CareShell({ children, noNav = false }) {
    const loc = useLocation();
    const nav = useNavigate();
    const { mode, setMode } = useMode();
    const { user } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [location, setLocation] = useState("Jodhpur");
    const geo = useGeo();
    const [unread, setUnread] = useState(0);
    const [locOpen, setLocOpen] = useState(false);

    useEffect(() => { if (mode !== "care") setMode("care"); }, []); // eslint-disable-line

    useEffect(() => {
        api.get("/service-areas").then((r) => {
            const a = r.data || [];
            if (a.length) setLocation(a.map((x) => x.cluster || x.name).slice(0, 2).join(" · "));
        }).catch(() => {});
        api.get("/notifications").then((r) => setUnread((r.data || []).filter((n) => !n.read).length)).catch(() => {});
    }, []);

    return (
        <div className="mx-auto min-h-screen max-w-md bg-[#FFFAF5]">
            <header className="sticky top-0 z-30 bg-[#FFFAF5]/95 px-5 pb-3 pt-4 backdrop-blur">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button data-testid="care-menu-btn" onClick={() => setMenuOpen(true)} className="rounded-xl p-1.5 text-navy transition-colors hover:bg-[#F43F5E]/10"><Menu size={22} /></button>
                        <Logo size={30} showText={false} />
                        <div>
                            <p className="font-heading text-base font-extrabold leading-none text-navy">HOMIIE</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button data-testid="care-location-pill" onClick={() => setLocOpen(true)} className="flex items-center gap-1 rounded-full bg-[#F43F5E]/10 px-2.5 py-1.5 text-[11px] font-semibold text-navy transition-colors hover:bg-[#F43F5E]/15">
                            <MapPin size={12} className="text-[#F43F5E]" /> <span className="max-w-[110px] truncate">{geoLabel(geo) || location}</span>
                        </button>
                        <button data-testid="care-notif-btn" onClick={() => nav("/notifications")} className="relative rounded-full bg-[#F43F5E]/10 p-2.5 text-navy transition-colors hover:bg-[#F43F5E]/15">
                            <Bell size={17} />
                            {unread > 0 && <span data-testid="care-notif-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#F43F5E] px-1 text-[9px] font-bold text-white">{unread}</span>}
                        </button>
                    </div>
                </div>
                <ModeSwitch full className="mt-3" />
                {user?.guardian_of && user?.parent && (
                    <button onClick={() => nav("/care/family")} data-testid="guardian-banner" className="mt-2 flex w-full items-center gap-2 rounded-xl bg-[#F43F5E]/10 px-3 py-2 text-left">
                        <Users size={15} className="text-[#F43F5E]" />
                        <span className="text-[12px] font-semibold text-navy">Managing <span className="text-[#F43F5E]">{user.parent.name}</span>'s Care</span>
                    </button>
                )}
            </header>
            <CareAreaBanner />

            <div className={noNav ? "" : "pb-24"}>{children}</div>
            {user?.role === "customer" && !noNav && <CareSosButton />}
            {locOpen && <LocationSheet accent="care" onClose={() => setLocOpen(false)} />}

            {!noNav && (
                <nav className="fixed bottom-0 left-0 right-0 z-40 mx-auto max-w-md border-t border-[#F43F5E]/10 bg-white px-2 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(244,63,94,0.08)]">
                    <div className="flex items-stretch justify-around">
                        {CARE_NAV.map((it) => {
                            const active = it.exact ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
                            const Icon = it.icon;
                            return (
                                <NavLink key={it.to} to={it.to} data-testid={it.testid} className="flex flex-1 flex-col items-center gap-1 py-2.5">
                                    <Icon size={22} strokeWidth={active ? 2.4 : 1.8} className={active ? "text-[#F43F5E]" : "text-navy/40"} />
                                    <span className={`text-[11px] font-semibold ${active ? "text-[#F43F5E]" : "text-navy/45"}`}>{it.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </nav>
            )}

            <CareMenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} nav={nav} />
        </div>
    );
}

function CareMenuDrawer({ open, onClose, nav }) {
    const { user, logout } = useAuth();
    const { setMode } = useMode();
    if (!open) return null;
    const go = (to) => { onClose(); nav(to); };
    const Item = ({ icon: Icon, label, onClick, testid }) => (
        <button data-testid={testid} onClick={onClick} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-navy transition-colors hover:bg-[#F43F5E]/5">
            <Icon size={18} className="text-[#F43F5E]" /> {label}
        </button>
    );
    return (
        <div className="fixed inset-0 z-[60] flex" onClick={onClose}>
            <div className="h-full w-72 max-w-[80%] animate-slide-in bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Logo size={28} showText={false} /><span className="font-heading font-extrabold text-navy">HOMIIE Care</span></div>
                    <button data-testid="care-menu-close-btn" onClick={onClose} className="rounded-full bg-[#F43F5E]/10 p-1.5"><X size={16} /></button>
                </div>
                <div className="mt-5 space-y-1">
                    <Item icon={Home} label="Care Home" testid="care-menu-home" onClick={() => go("/care")} />
                    <Item icon={ShieldPlus} label="Care Plans" testid="care-menu-plans" onClick={() => go("/care/plans")} />
                    <Item icon={HeartPulse} label="Health Records" testid="care-menu-health" onClick={() => go("/care/health")} />
                    <Item icon={CalendarCheck} label="Bookings" testid="care-menu-bookings" onClick={() => go("/care/bookings")} />
                    <Item icon={Wallet} label="Care Wallet" testid="care-menu-payments" onClick={() => go("/wallet")} />
                    <Item icon={Users} label="Family & Guardians" testid="care-menu-family" onClick={() => go("/care/family")} />
                    <WhatsAppAssistant accent="care" drawerItemClass="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-navy transition-colors hover:bg-[#F43F5E]/5" iconClass="text-[#F43F5E]" />
                    <Item icon={Wrench} label="Switch to Services" testid="care-menu-services" onClick={() => { setMode("services"); go("/"); }} />
                    <Item icon={Scale} label="Legal & Policies" testid="care-menu-legal" onClick={() => go("/legal")} />
                </div>
                <div className="mt-4 border-t border-border pt-4">
                    <div className="mb-2 flex items-center justify-between px-3"><span className="text-xs font-semibold text-navy/50">Language</span><LanguageToggle /></div>
                    {user && <Item icon={LogOut} label="Logout" testid="care-menu-logout" onClick={() => { logout(); onClose(); nav("/login"); }} />}
                </div>
            </div>
        </div>
    );
}
